import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const CommentPopover = ({ anchorRef, comments = [], onAddComment, onClose }) => {
  const popoverRef = useRef(null);
  const [input, setInput] = useState("");
  const [style, setStyle] = useState({ top: 0, left: 0 });
  const [avatars, setAvatars] = useState({});


  // 📍 Position the popover above the button
  useEffect(() => {
  if (anchorRef.current) {
    const rect = anchorRef.current.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft = window.pageXOffset || document.documentElement.scrollLeft;

    const popoverWidth = 288; // width: w-72 = 18rem = 288px
    const verticalOffset = 12; // space between button and box

    setStyle({
      top: rect.top + scrollTop - 200 - verticalOffset, // 200 = estimated height of box
      left: rect.left + scrollLeft - popoverWidth / 2 + rect.width / 2 - 8, // shift left slightly
    });
  }
}, [anchorRef]);

  // 🧼 Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  // Fetch user profileImages
  useEffect(() => {
  const fetchAvatars = async () => {
    const missingUIDs = comments
      .filter((c) => typeof c !== "string" && c.userId && !avatars[c.userId])
      .map((c) => c.userId);

    const uniqueUIDs = [...new Set(missingUIDs)];
    const fetched = {};

    await Promise.all(
      uniqueUIDs.map(async (uid) => {
        try {
          const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/user/uid/${uid}`);
          const data = await res.json();
          if (res.ok) {
            fetched[uid] = {
              image: data.profileImage || "https://via.placeholder.com/40",
              name: data.firstName || "Unknown",
              email: data.email || null,
            };
          }
        } catch {
          fetched[uid] = {
            image: "https://via.placeholder.com/40",
            name: "Unknown",
            email: null, 
          };
        }
      })
    );

    setAvatars((prev) => ({ ...prev, ...fetched }));
  };

  if (comments.length > 0) fetchAvatars();
}, [comments]);

  return createPortal(
    <div
      ref={popoverRef}
      className="absolute z-[9999] w-72 bg-white rounded-xl shadow-xl p-4 border border-gray-200"
      style={{ position: "absolute", ...style }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-gray-700 font-bold mb-2">Comments</div>

      <div className="max-h-40 overflow-y-auto space-y-2 mb-2">
        {comments.length === 0 ? (
          <p className="text-sm text-gray-500">No comments yet.</p>
        ) : (
          comments.map((c, i) => (
            <div key={i} className="text-sm text-gray-800 border-b pb-1">
              {typeof c === "string" ? (
                c
                ) : (
                  <div className="flex items-start gap-2">
                    {/* User Avatar */}
                    <img
                      src={avatars[c.userId]?.image || "https://via.placeholder.com/40"}
                      alt="avatar"
                      className="w-6 h-6 rounded-full object-cover mt-0.5"
                    />

                    {/* Text ( Username + Comment ) */}
                    <div>
                      {/* Username */}
                      <div
                        className="font-medium text-gray-600 hover:underline cursor-pointer"
                        onClick={() => {
                          const email = avatars[c.userId]?.email;
                          if (email) {
                            const username = email.split("@")[0];
                            window.location.href = `/${username}`;
                          }
                        }}
                      >
                        {avatars[c.userId]?.name || c.firstName}
                      </div>

                      {/* Comment Text */}
                      <div className="text-gray-800 mt-0.5">
                        {c.text}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
      </div>

      <textarea
        value={input}
        onChange={(e) => setInput(e.target.value)}
        rows={2}
        placeholder="Leave a comment..."
        className="w-full border rounded px-2 py-1 text-sm mb-2 resize-none"
      />

      <div className="flex justify-end gap-2">
        <button
          onClick={onClose}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          Cancel
        </button>
        <button
          onClick={() => {
            if (input.trim()) {
              onAddComment(input.trim());
              setInput("");
            }
          }}
          className="text-xs bg-blue-500 text-white px-3 py-1 rounded hover:bg-blue-600"
        >
          Comment
        </button>
      </div>
    </div>,
    document.body
  );
};

export default CommentPopover;
